import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CourseAssignmentDetail } from "@/services/studentReportingService";
import { reportExportUtils } from "@/utils/reportExportUtils";
import StaffDownloadService from "@/services/staffDownloadService";
import { toast } from "@/components/ui/sonner";
import { Award, BookOpen, Clock, TrendingUp, FileText, Loader2, FileArchive } from "lucide-react";

interface IndividualStudentTableProps {
  data: CourseAssignmentDetail[];
}

export const IndividualStudentTable = ({ data }: IndividualStudentTableProps) => {
  const [downloadingCert, setDownloadingCert] = useState<string | null>(null);
  const [downloadingMarksheet, setDownloadingMarksheet] = useState<string | null>(null);
  const [bulkDownloading, setBulkDownloading] = useState<'certs' | 'marks' | null>(null);

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No course assignments found for this student
      </div>
    );
  }

  const studentName = data[0].studentName;
  const studentEmail = data[0].studentEmail;
  const studentId = data[0].studentId;

  // Calculate summary stats
  const totalCourses = data.length;
  const completedCourses = data.filter((d) => d.status === 'completed').length;
  const inProgressCourses = data.filter((d) => d.status === 'in_progress').length;
  const certificatesEarned = data.filter((d) => d.hasCertificate).length;
  const totalTimeSpent = data.reduce((sum, d) => sum + d.timeSpent, 0);
  const averageGrade =
    data.filter((d) => d.finalGrade !== null).length > 0
      ? data.reduce((sum, d) => sum + (d.finalGrade || 0), 0) /
        data.filter((d) => d.finalGrade !== null).length
      : null;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-success">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-warning">In Progress</Badge>;
      case 'active':
        return <Badge variant="outline">Active</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleDownloadCertificate = async (courseId: string) => {
    setDownloadingCert(courseId);
    try {
      const cert = await StaffDownloadService.getCertificateForStudent(studentId, courseId);
      if (cert) {
        await StaffDownloadService.downloadCertificatePDF(cert.id);
        toast.success('Certificate downloaded successfully');
      } else {
        toast.error('Certificate not found');
      }
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    } finally {
      setDownloadingCert(null);
    }
  };

  const handleDownloadMarksheet = async (courseId: string, courseTitle: string) => {
    setDownloadingMarksheet(courseId);
    try {
      await StaffDownloadService.downloadMarksheetPDF(studentId, courseId, courseTitle, studentName);
      toast.success('Marksheet downloaded successfully');
    } catch (error) {
      console.error('Error downloading marksheet:', error);
      toast.error('Failed to download marksheet');
    } finally {
      setDownloadingMarksheet(null);
    }
  };

  const handleBulkDownloadCertificates = async () => {
    setBulkDownloading('certs');
    try {
      await StaffDownloadService.downloadAllCertificatesAsZip(studentId, studentName);
      toast.success('All certificates downloaded');
    } catch (error) {
      console.error('Error downloading certificates:', error);
      toast.error('Failed to download certificates');
    } finally {
      setBulkDownloading(null);
    }
  };

  const handleBulkDownloadMarksheets = async () => {
    setBulkDownloading('marks');
    try {
      const courses = data
        .filter(d => d.status === 'completed')
        .map(d => ({ courseId: d.courseId, courseTitle: d.courseTitle }));
      await StaffDownloadService.downloadAllMarksheetsAsZip(studentId, studentName, courses);
      toast.success('All marksheets downloaded');
    } catch (error) {
      console.error('Error downloading marksheets:', error);
      toast.error('Failed to download marksheets');
    } finally {
      setBulkDownloading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Student Header Card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-2xl">{studentName}</CardTitle>
            <p className="text-muted-foreground">{studentEmail}</p>
          </div>
          {certificatesEarned > 0 && (
            <div className="flex gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBulkDownloadCertificates}
                      disabled={bulkDownloading !== null}
                    >
                      {bulkDownloading === 'certs' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <FileArchive className="h-4 w-4 mr-2" />
                      )}
                      All Certs
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download all certificates as ZIP</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBulkDownloadMarksheets}
                      disabled={bulkDownloading !== null}
                    >
                      {bulkDownloading === 'marks' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <FileArchive className="h-4 w-4 mr-2" />
                      )}
                      All Marks
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download all marksheets as ZIP</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{totalCourses}</p>
                <p className="text-xs text-muted-foreground">Total Courses</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-success" />
              <div>
                <p className="text-2xl font-bold">{completedCourses}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-warning" />
              <div>
                <p className="text-2xl font-bold">{inProgressCourses}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Award className="h-8 w-8 text-accent" />
              <div>
                <p className="text-2xl font-bold">{certificatesEarned}</p>
                <p className="text-xs text-muted-foreground">Certificates</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                {averageGrade ? Math.round(averageGrade) : '-'}
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {reportExportUtils.formatPercentage(averageGrade)}
                </p>
                <p className="text-xs text-muted-foreground">Avg Grade</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Course Assignments Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Course Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Assigned Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Completion Date</TableHead>
              <TableHead className="text-right">Grade</TableHead>
              <TableHead className="text-right">Time Spent</TableHead>
              <TableHead className="text-center">Certificate</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((assignment) => (
              <TableRow key={assignment.id}>
                <TableCell className="font-medium">{assignment.courseTitle}</TableCell>
                <TableCell>
                  <Badge variant="outline">{assignment.category}</Badge>
                </TableCell>
                <TableCell className="capitalize">{assignment.level}</TableCell>
                <TableCell>{reportExportUtils.formatDate(assignment.assignedDate)}</TableCell>
                <TableCell>{getStatusBadge(assignment.status)}</TableCell>
                <TableCell>{reportExportUtils.formatDate(assignment.completionDate)}</TableCell>
                <TableCell className="text-right">
                  {reportExportUtils.formatPercentage(assignment.finalGrade)}
                </TableCell>
                <TableCell className="text-right">
                  {reportExportUtils.formatDuration(assignment.timeSpent)}
                </TableCell>
                <TableCell>
                  {assignment.hasCertificate ? (
                    <div className="flex items-center gap-1 justify-center">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDownloadCertificate(assignment.courseId)}
                              disabled={downloadingCert === assignment.courseId}
                            >
                              {downloadingCert === assignment.courseId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Award className="h-4 w-4 text-success" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Download Certificate</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDownloadMarksheet(assignment.courseId, assignment.courseTitle)}
                              disabled={downloadingMarksheet === assignment.courseId}
                            >
                              {downloadingMarksheet === assignment.courseId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileText className="h-4 w-4 text-primary" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Download Marksheet</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-muted-foreground cursor-help">-</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Complete course to earn certificate</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
